// Entry point for the local dev mock. Import this from `src/index.tsx` once
// at app boot; it self-gates on `import.meta.env.VITE_MOCK === '1'` and
// otherwise does nothing (no runtime cost in production builds).
//
// Security property: when VITE_MOCK=1 we replace window.fetch so the page
// cannot reach the API Gateway or any real backend. The interceptor throws on
// any unregistered URL instead of falling through.

import { installFetchInterceptor } from './fetchInterceptor';
import { buildRoutes } from './handlers';
import { loadMockSeeds } from './seeds';
import { activeFaults, clearFaults } from './faults';

function renderBanner(
  message: string,
  tone: 'info' | 'error',
  action?: { label: string; onClick: () => void },
): void {
  if (typeof document === 'undefined') return;
  const el = document.createElement('div');
  el.textContent = message;
  if (action) {
    const btn = document.createElement('button');
    btn.textContent = action.label;
    btn.style.cssText =
      'margin-left:12px; padding:2px 10px; border-radius:4px; border:1px solid rgba(255,255,255,.6);' +
      'background:transparent; color:#fff; font:inherit; cursor:pointer;';
    btn.onclick = () => {
      action.onClick();
      el.remove();
    };
    el.appendChild(btn);
  }
  const bg = tone === 'error' ? '#991b1b' : '#1d4ed8';
  // Static positioning + prepended to body so it sits in normal flow ABOVE
  // the app root, pushing content down instead of overlaying it.
  el.style.cssText = `
    padding:8px 16px; font:13px/1.4 system-ui, sans-serif;
    color:#fff; background:${bg}; text-align:center;
  `;
  const mount = () => document.body.prepend(el);
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);
}

/**
 * Install the mock. Returns a promise that resolves when routes are live.
 * Callers should `await` this before rendering anything that hits fetch —
 * AuthProvider fires `tryRestore()` in its mount effect, which goes straight
 * through fetch, so the interceptor needs to be ready by that point.
 */
export async function installMockIfEnabled(): Promise<void> {
  if (import.meta.env.VITE_MOCK !== '1') return;

  try {
    const state = await loadMockSeeds();
    const routes = buildRoutes(state);
    installFetchInterceptor(routes);
    // Active fault injection has to be impossible to miss — a deliberately
    // broken hydration looks exactly like a real bug otherwise.
    const faults = activeFaults();
    if (faults.length > 0) {
      // The banner carries its own clear button. Clearing via the URL would
      // mean a reload, and the access token is memory-only — you would be
      // logged out and lose your place, which makes the recovery path
      // (fault -> Retry -> healthy) impossible to actually watch.
      renderBanner(
        `[DEV MOCK · FAULTS ACTIVE] ${faults.join('  ·  ')}`,
        'error',
        { label: 'Clear faults', onClick: () => clearFaults() },
      );
    }
    // Console handle for finer control, e.g. window.__devMockFaults.clear('mockSave')
    (window as unknown as Record<string, unknown>).__devMockFaults = {
      list: activeFaults,
      clear: clearFaults,
    };
    renderBanner(
      `[DEV MOCK] Network blocked — ${routes.length} mock routes loaded. User: ${state.currentUserEmail}`,
      'info',
    );
    console.info('[devMock] installed', {
      routes: routes.length,
      requestSets: state.requestSets.length,
      docs: Object.keys(state.docsById).length,
      matches: Object.values(state.matchesBySetId).reduce((n, l) => n + l.length, 0),
    });
  } catch (err) {
    const hint = 'Did you run `npm run setup-mock`? See src/lib/devMock/README.md.';
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[devMock] install failed:', err);
    renderBanner(`[DEV MOCK] failed to load seeds: ${msg}. ${hint}`, 'error');
    // Intentionally leave window.fetch unpatched so the dev can still see
    // what URLs the app *would* hit, but the banner makes the failure loud.
  }
}
